import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// 页面组件
import LoginPage from './pages/login/LoginPage';
import DashboardLayout from './pages/(dashboard)/layout';
import DashboardPage from './pages/(dashboard)/page';
import { useAuthStore } from './stores';

// 懒加载子页面
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const ReportsNewPage = lazy(() => import('./pages/reports-new/ReportsNewPage'));
const DimensionsPage = lazy(() => import('./pages/dimensions/DimensionsPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const ReportDetailPage = lazy(() => import('./pages/reports/ReportDetailPage'));
const ReportEditPage = lazy(() => import('./pages/reports/ReportEditPage'));
const CreateReportPage = lazy(() => import('./pages/reports/CreateReportPage'));
const ImportsPage = lazy(() => import('./pages/imports/ImportsPage'));

// 内容分析页面
const ArticlesPage = lazy(() => import('./pages/content/ArticlesPage'));
const ArticleExtensionsPage = lazy(() => import('./pages/content/ArticleExtensionsPage'));
const MenuStatsPage = lazy(() => import('./pages/content/MenuStatsPage'));
const MessageStatsPage = lazy(() => import('./pages/content/MessageStatsPage'));
const MessageKeywordsPage = lazy(() => import('./pages/content/MessageKeywordsPage'));
const ChannelReadSourcePage = lazy(() => import('./pages/content/ChannelReadSourcePage'));
const ShareStatsPage = lazy(() => import('./pages/content/ShareStatsPage'));
const CentralMediaPage = lazy(() => import('./pages/content/CentralMediaPage'));
const ArticleCommentsPage = lazy(() => import('./pages/content/ArticleCommentsPage'));
const ContentThemesPage = lazy(() => import('./pages/content/ContentThemesPage'));
const ArticleUserDemographicsPage = lazy(() => import('./pages/content/ArticleUserDemographicsPage'));

// 粉丝分析页面
const FanGrowthPage = lazy(() => import('./pages/fan/FanGrowthPage'));
const UserActivityPage = lazy(() => import('./pages/fan/UserActivityPage'));
const FanProfilePage = lazy(() => import('./pages/fan/FanProfilePage'));
const FanGrowthEventsPage = lazy(() => import('./pages/fan/FanGrowthEventsPage'));

// 平台统计页面
const PlatformStatsPage = lazy(() => import('./pages/platform/PlatformStatsPage'));

// 高校对比页面
const PeerUniversityStatsPage = lazy(() => import('./pages/peer/PeerUniversityStatsPage'));

// 系统设置页面
const SettingsDimensionsPage = lazy(() => import('./pages/settings/DimensionsPage'));
const SettingsRolesPage = lazy(() => import('./pages/settings/RolesPage'));
const PromptTemplatesPage = lazy(() => import('./pages/settings/PromptTemplatesPage'));
const WechatAccountCategoriesPage = lazy(() => import('./pages/settings/WechatAccountCategoriesPage'));
const WechatAccountsPage = lazy(() => import('./pages/content/WechatAccountsPage'));

// 创建 QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// 路由守卫组件
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isHydrated } = useAuthStore();
  
  // 等待 hydration 完成
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

// 公开路由守卫（已登录用户不能访问登录页）
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isHydrated } = useAuthStore();
  
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }
  
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          }
        >
          <Routes>
            {/* 登录页 - 公开路由 */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />

            {/* 管理后台 - 需要登录 */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <DashboardLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="dimensions" element={<DimensionsPage />} />
              
              {/* 内容分析路由 */}
              <Route path="content/articles" element={<ArticlesPage />} />
              <Route path="content/article-extensions" element={<ArticleExtensionsPage />} />
              <Route path="content/menu" element={<MenuStatsPage />} />
              <Route path="content/message" element={<MessageStatsPage />} />
              <Route path="content/message-keywords" element={<MessageKeywordsPage />} />
              <Route path="content/share-stats" element={<ShareStatsPage />} />
              <Route path="content/central-media" element={<CentralMediaPage />} />
              <Route path="content/article-comments" element={<ArticleCommentsPage />} />
              <Route path="content/content-themes" element={<ContentThemesPage />} />
              <Route path="content/article-demographics" element={<ArticleUserDemographicsPage />} />
              <Route path="content/wechat-accounts" element={<WechatAccountsPage />} />

              {/* 渠道分析路由 */}
              <Route path="channel/read-sources" element={<ChannelReadSourcePage />} />
              
              {/* 粉丝分析路由 */}
              <Route path="fan/growth" element={<FanGrowthPage />} />
              <Route path="fan/activity" element={<UserActivityPage />} />
              <Route path="fan/profile" element={<FanProfilePage />} />
              <Route path="fan/growth-events" element={<FanGrowthEventsPage />} />

              {/* 平台统计路由 */}
              <Route path="platform/stats" element={<PlatformStatsPage />} />

              {/* 高校对比路由 */}
              <Route path="peer/university-stats" element={<PeerUniversityStatsPage />} />

              {/* 系统设置路由 */}
              <Route path="settings/dimensions" element={<SettingsDimensionsPage />} />
              <Route path="settings/users" element={<UsersPage />} />
              <Route path="settings/roles" element={<SettingsRolesPage />} />
              <Route path="settings/prompt-templates" element={<PromptTemplatesPage />} />
              <Route path="settings/wechat-account-categories" element={<WechatAccountCategoriesPage />} />
              
              {/* 报告管理路由 */}
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports-new" element={<ReportsNewPage />} />
              <Route path="reports/create" element={<CreateReportPage />} />
              <Route path="reports/:id" element={<ReportDetailPage />} />
              <Route path="reports/:id/edit" element={<ReportEditPage />} />
              
              <Route path="imports" element={<ImportsPage />} />
            </Route>

            {/* 404 重定向 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
